import { Grid, Table, Typography } from "antd"
import type { TableColumnsType } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { TABLE_SURFACES } from "../../EntityTable/surfaces.ts"
import { ResizableHeaderCell } from "../../Table/ResizableHeaderCell.tsx"
import { useColumnResize } from "../../../hooks/use-column-resize.tsx"
import { useTableColumnConfig } from "../../../hooks/use-table-column-config.tsx"
import { RowCardList } from "../rows/RowCardList"
import { neutral } from "../tokens"
import { compareCellValues, formatCellValue, rowDrillTarget, rowsPayloadOf } from "../rowsFormat"
import type { RowRecord, WidgetProps, WidgetState } from "../types"
import { WidgetFrame } from "../WidgetFrame"

export const TableWidget = ({
  widget,
  payload,
  state,
  currency,
  onRefresh,
  onExpand,
  onDrill
}: WidgetProps) => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const screens = Grid.useBreakpoint()
  const data = payload ? rowsPayloadOf(payload) : undefined
  const title = widget.config?.title ?? widget.title
  const effectiveCurrency = currency ?? payload?.currency

  let effectiveState: WidgetState
  if (state && state !== "ready") {
    effectiveState = state
  } else if (!data) {
    effectiveState = "loading"
  } else if (data.rows.length === 0) {
    effectiveState = "empty"
  } else {
    effectiveState = "ready"
  }

  const fullColumns: TableColumnsType<RowRecord> = (data?.columns ?? []).map((col) => ({
    key: col.key,
    dataIndex: col.key,
    title: col.title,
    sorter: (a, b) => compareCellValues(a[col.key], b[col.key], col.unit),
    render: (value: unknown) =>
      formatCellValue(value, col.unit, {
        currency: effectiveCurrency,
        language: i18n.language
      })
  }))

  // Keyed per widget INSTANCE (widget.id), not per widget type: the same
  // TableWidget component renders every "table" widget on a dashboard, each
  // with its own server-driven column set, so one user's resize of "Top
  // overdue invoices" must not touch "Recent leads".
  const { columns, fullConfig, setColumns } = useTableColumnConfig(
    TABLE_SURFACES.dashboardWidgetTable,
    widget.id,
    fullColumns
  )
  const { tableWrapRef, decorateColumns } = useColumnResize<RowRecord>({
    fullConfig,
    setColumns
  })

  const rows = (data?.rows ?? []).map((row, index) => ({
    key: String((row as { id?: unknown }).id ?? index),
    ...row
  }))

  const goTo = (target: string) => (onDrill ? onDrill(target) : navigate(target))

  // UI_UX_GUIDELINES §5.1: below md a table renders as cards, not as a scroller.
  const asCards = !screens.md

  const limit = widget.config?.limit
  const total = data?.total
  const shown = rows.length
  const showTotalCaption = typeof total === "number" && total > shown

  return (
    <WidgetFrame
      title={title}
      state={effectiveState}
      capabilities={widget.capabilities}
      widgetId={widget.id}
      payload={payload}
      onRefresh={onRefresh}
      onExpand={onExpand}
    >
      {asCards && data ? (
        <RowCardList
          data={data}
          currency={effectiveCurrency}
          pageSize={limit}
          onOpen={goTo}
        />
      ) : (
        <div ref={tableWrapRef}>
          <Table
            size="small"
            columns={decorateColumns(columns)}
            components={{ header: { cell: ResizableHeaderCell } }}
            dataSource={rows}
            // Without an explicit x, rc-table builds no horizontal scroller and the
            // table paints past the card border rather than shrinking to it.
            scroll={{ x: "max-content" }}
            pagination={limit ? { pageSize: limit, hideOnSinglePage: true } : false}
            onRow={(row) => {
              const target = rowDrillTarget(row)
              if (!target) return {}
              return {
                style: { cursor: "pointer" },
                onClick: () => goTo(target)
              }
            }}
          />
        </div>
      )}
      {showTotalCaption && (
        <Typography.Text
          data-testid="table-total-caption"
          style={{ display: "block", marginTop: 8, fontSize: 12, color: neutral.muted }}
        >
          {t("widget.table.showingOfTotal", "Showing {{shown}} of {{total}}", {
            shown,
            total
          })}
        </Typography.Text>
      )}
    </WidgetFrame>
  )
}
